import { useEffect, useReducer, useRef, useState } from "react";
import { Bgl, Bp, Insulin, Observation, Weight } from "../../types/observation";
import { FormatDateForDisplay, ProcessObservations } from "../../util/data-utils";
import { Button, Card, Col, Row, Table } from "react-bootstrap";
import { CANCEL, LINK, LINK_ALL, LINK_OBSERVATIONS, METFORMIN, PATIENT_LINK_OBSERVATION, SELECTED_PARTICIPANT } from "../../util/const";
import { Participant } from "../../types/participant";
import ResourceCheckbox from "../../Components/ResourceCheckbox";
import React from "react";
import { DatabaseManager } from "../../services/DatabaseManager";

export type LinkObservationsProps = {
  toggleDisplay: (cancelAll: boolean) => void;
  participant: Participant;
  observations: Array<Observation>;
};

const LinkObservations = (props: LinkObservationsProps) => {

  const [data, setData] = useState<Map<string, Array<Observation>>>();

  const linkingObservations = useRef<Observation[]>(props.observations);
  const linkState = { linkAll: true, linkingObservations: props.observations }
  const enum REDUCER_ACTION_TYPE {
    TOGGLE_LINK_ALL,
    TOGGLE_LINK_OBSERVATION,
    DATA_LOADED,
  }
  type ReducerAction = {
    type: REDUCER_ACTION_TYPE,
    payload?: Observation[]
  }
  const reducer = (state: typeof linkState, action: ReducerAction): typeof 
  linkState => {
    switch(action.type)
    {
      case REDUCER_ACTION_TYPE.TOGGLE_LINK_ALL: {
        state.linkAll ? linkingObservations.current = [] : linkingObservations.current = props.observations;
        if(state.linkAll) return  {...state, linkAll: false, linkingObservations: linkingObservations.current}
        else return {...state, linkAll: true, linkingObservations: linkingObservations.current}
      }        
      case REDUCER_ACTION_TYPE.TOGGLE_LINK_OBSERVATION: {
        linkingObservations.current = action.payload!;
        return {...state, linkAll: linkingObservations.current.length === props.observations.length, linkingObservations: linkingObservations.current}
      }
      case REDUCER_ACTION_TYPE.DATA_LOADED: {
        linkingObservations.current = action.payload!;
        setData(ProcessObservations(linkingObservations.current));
        return {...state, linkAll: true, linkingObservations: linkingObservations.current}
      }
      default: throw Error();
    }
  }
  const [link, dispatch] = useReducer(reducer, linkState);

  useEffect(() => {
    if(props.observations.length > 0)
      dispatch({type: REDUCER_ACTION_TYPE.DATA_LOADED, payload: props.observations}) 
  }, [props.observations, REDUCER_ACTION_TYPE.DATA_LOADED]);

  const toggleLinkObservation = (observation: Observation, checked: boolean) => {
    let temp = linkingObservations.current;
    if (checked) 
    {
      const index = temp.indexOf(observation);
      if (index > -1) temp = temp.filter((linkObservation: Observation) => linkObservation !== observation);
    } 
    else 
    {
      temp.push(observation);
      temp.sort((a: Observation, b: Observation) => a.resourceId.localeCompare(b.resourceId));
    }
    dispatch({type: REDUCER_ACTION_TYPE.TOGGLE_LINK_OBSERVATION, payload: temp})
  };

  const toggleLinkAll = () => {
    dispatch({type: REDUCER_ACTION_TYPE.TOGGLE_LINK_ALL})
  };

  const handleLinkClicked = () =>
  {
    if(linkingObservations.current !== undefined && linkingObservations.current.length > 0)
    {
      /// The patientResourceId is set on each observation when the
      /// data is loaded, so no need to pass it as a path variable
      DatabaseManager.getInstance()
        .post(PATIENT_LINK_OBSERVATION, linkingObservations.current)
        .then((response) => {
          props.toggleDisplay(true);
        })
        .catch(() => {
          console.log("link observation error");
        });
    }
  }

  const checkboxCell = (item: Observation) => {
    return (<td className="small-col centre">
      <ResourceCheckbox
        resource={item}
        toggleResource={toggleLinkObservation}
        checked={link.linkingObservations.findIndex((i: { documentId: string; }) => i.documentId===item.documentId) > -1}
      />
    </td>);
  }

  const toggleAllChecked = () => {
    if(props.observations === undefined || linkingObservations.current === undefined)
      return false;
    return linkingObservations.current.length === props.observations.length && props.observations.length > 0;
  }

  /// There isn't any data, go home
  if(data === undefined)
    return (<></>);
  else
  {
    var bgls = data?.has("bgl") ? data?.get("bgl") as Array<Bgl> : [];
    var bps = data?.has("bp") ? data?.get("bp") as Array<Bp> : [];
    var meds = data?.has("insulin") ? data?.get("insulin") as Array<Insulin> : [];
    var weights = data?.has("weight") ? data?.get("weight") as Array<Weight> : [];

    /// There is data, display it
    return (
      <Card className="mt-3" border="secondary">
        <Card.Header className="fs-6">
          <Row>
            <Col>{LINK_OBSERVATIONS}</Col>
            <Col className="right">
              <Button
                type="button"
                onClick={(e) => props.toggleDisplay(false)}
                className="fs-6 btn-color me-2">
                {CANCEL}
              </Button>
              <Button
                type="button"
                onClick={(e) => handleLinkClicked()}
                className="fs-6 btn-color me-2">
                {LINK}
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="color-text fs-6">
          <Col className="mt-3">
            <Table bordered hover striped className="review-table">
              <thead className="table-header">
                <tr>
                  <th colSpan={5} className="left">{SELECTED_PARTICIPANT}: {props.participant.givenName} {props.participant.familyName} | DOB: {FormatDateForDisplay(props.participant.birthDate)} | Email: {props.participant.email}</th>
                </tr>
                <tr>
                  <th colSpan={5} className="section"></th>
                </tr>
                <tr>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th className="small-col centre">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="linkAll"
                      checked={toggleAllChecked()}
                      onChange={toggleLinkAll}
                    />
                    <span className="ms-3">{LINK_ALL}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
              { bgls?.map((item: Bgl, i: number) => {
                return (
                  <React.Fragment key={item.documentId}>
                  <tr className="middle">
                    <td className="med-col centre">{FormatDateForDisplay(item.recordedDate)}</td>
                    <td className="med-col left">{item.context}</td>
                    <td className="med-col left">{item.reading} mmol/L</td>
                    <td className="left">{item.notes}</td>
                    {checkboxCell(item)}
                  </tr>
                  </React.Fragment>
                );  
              })}
              { bps?.map((item: Bp, i: number) => {
                return (
                  <React.Fragment key={item.documentId}>
                  <tr className="middle">
                    <td className="med-col centre">{FormatDateForDisplay(item.recordedDate)}</td>
                    <td className="med-col left">{item.systolic}/{item.diastolic} mmHg</td>
                    {
                      item.heartRate ? <td className="med-col left">{item.heartRate} bpm</td> : 
                      <td></td>
                    }
                    <td></td>
                    {checkboxCell(item)}
                  </tr>
                  </React.Fragment>
                );  
              })}
              { meds?.map((item: Insulin, i: number) => {
                return (
                  <React.Fragment key={item.documentId}>
                  <tr className="middle">
                    <td className="med-col centre">{FormatDateForDisplay(item.recordedDate)}</td>
                    {
                      item.name ? <td className="med-col left">{item.name}: {item.dose}units</td> : 
                      <td></td>
                    }
                    {
                      item.metforminDose ? <td className="med-col left">{METFORMIN}: {item.metforminDose}mg</td> : 
                      <td></td>
                    }
                    <td></td>
                    {checkboxCell(item)}
                  </tr>
                  </React.Fragment>
                );  
              })}
              { weights?.map((item: Weight, i: number) => {
                return (
                  <React.Fragment key={item.documentId}>
                  <tr className="middle">
                    <td className="med-col centre">{FormatDateForDisplay(item.recordedDate)}</td>
                    <td className="med-col left">{item.reading}Kg</td>
                    <td className="med-col left">{item.enterBy} Entered</td>
                    <td></td>
                    {checkboxCell(item)}
                  </tr>
                  </React.Fragment>
                );  
              })}
              </tbody>
            </Table>
          </Col>
        </Card.Body>
      </Card>
    );
  }
}

export default LinkObservations;